import { all, takeLatest, put, call, select } from 'redux-saga/effects';
import queryString from 'query-string';
import { selectPolicy } from '../policy/selectors';
import renderNotification from '../../helpers/notifications/renderNotification';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import { selectPoliciesActiveFilters } from '../filters/selectors';
import { onGetPolicies } from '../policies/api';
import { reloadPolicies } from '../policies/slice';
import {
  onGetInsuredRisksPairs,
  onGetInsuredRisksToPolicies,
  onCreateInsuredRisksToPolicy,
  onUpdateInsuredRisksToPolicy,
  onDeleteInsuredRisksForPolicy
} from './api';
import {
  getInsuredRisksForSelectedPolicy,
  getInsuredRisksForSelectedPolicySucceeded,
  getInsuredRisksForSelectedPolicyFailed,
  getInsuredRisksFromManyPolicies,
  getInsuredRisksFromManyPoliciesSucceeded,
  getInsuredRisksFromManyPoliciesFailed,
  addInsuredRisksToPolicy,
  addInsuredRisksToPolicySucceeded,
  addInsuredRisksToPolicyFailed,
  updateInsuredRisksToPolicy,
  updateInsuredRisksToPolicySucceeded,
  updateInsuredRisksToPolicyFailed,
  bulkAssignInsuredRisks,
  bulkAssignInsuredRisksSucceeded,
  bulkAssignInsuredRisksFailed,
  bulkUnassignInsuredRisks,
  bulkUnassignInsuredRisksSucceeded,
  bulkUnassignInsuredRisksFailed
} from './slice';

const {
  policies_bulk_actions_remove_insured_risks_success,
  policies_bulk_actions_remove_insured_risk_success,
  policies_bulk_actions_add_insured_risks_success
} = NOTIFICATION_MESSAGES;

export function* getInsuredRisksForSelectedPolicyWorker({ payload }) {
  const { policyId } = payload;
  try {
    const response = yield call(onGetInsuredRisksToPolicies, { policy_id: policyId });
    yield put(getInsuredRisksForSelectedPolicySucceeded(response.data));
  } catch {
    yield put(getInsuredRisksForSelectedPolicyFailed());
  }
}

export function* addInsuredRisksToPolicyWorker({ payload }) {
  const { insuredRisksToPolicy } = payload;
  try {
    yield call(onCreateInsuredRisksToPolicy, insuredRisksToPolicy);
    yield put(addInsuredRisksToPolicySucceeded());
  } catch {
    yield put(addInsuredRisksToPolicyFailed());
  }
}

export function* updateInsuredRisksToPolicyWorker({ payload }) {
  const { insuredRisks, callback } = payload;
  try {
    const { toUpdate, toDelete, toCreate } = insuredRisks;
    yield call(onUpdateInsuredRisksToPolicy, toUpdate);
    yield call(onDeleteInsuredRisksForPolicy, toDelete);
    yield call(onCreateInsuredRisksToPolicy, toCreate);

    yield put(updateInsuredRisksToPolicySucceeded());

    if (typeof callback === 'function') {
      callback();
    }
  } catch {
    yield put(updateInsuredRisksToPolicyFailed());
  }
}

export function* bulkAssignInsuredRisksToPolicyWorker({ payload }) {
  const { insuredRisks, callback } = payload;

  try {
    const policiesFilters = yield select(selectPoliciesActiveFilters);
    const params = queryString.parse(policiesFilters);
    params.paging = false;
    const policiesResponse = yield call(onGetPolicies, params);

    const policyIds = policiesResponse.data.map((policy) => policy.id);

    const insuredRisksToPolicies = [];
    policiesResponse.data.forEach((policy) => {
      insuredRisks.forEach(({ insured_risk_id, addendum, retention_amount }) => {
        insuredRisksToPolicies.push({
          policy_id: policy.id,
          insured_risk_id,
          addendum,
          retention_amount
        });
      });
    });

    const selectedInsuredRiskIds = insuredRisks.map(({ insured_risk_id }) => insured_risk_id);

    const existingPairsResponse = yield call(onGetInsuredRisksPairs, selectedInsuredRiskIds);

    const existingPairs = existingPairsResponse.data.filter((pair) => policyIds.includes(pair.policy_id));

    const pairsToUpdate = [];
    const pairsToCreate = insuredRisksToPolicies.filter((generatedPair) => {
      const foundPair = existingPairs.find(
        (existing) =>
          existing.policy_id === generatedPair.policy_id && existing.insured_risk_id === generatedPair.insured_risk_id
      );

      if (foundPair) {
        pairsToUpdate.push({
          id: foundPair.id,
          ...generatedPair
        });
      }
      return !foundPair;
    });

    if (pairsToCreate.length > 0) {
      yield call(onCreateInsuredRisksToPolicy, pairsToCreate);
    }
    if (pairsToUpdate.length > 0) {
      yield call(onUpdateInsuredRisksToPolicy, pairsToUpdate);
    }

    yield put(bulkAssignInsuredRisksSucceeded());
    if (typeof callback === 'function') callback();

    yield put(reloadPolicies());

    renderNotification({
      type: 'success',
      message: policies_bulk_actions_add_insured_risks_success
    });
  } catch {
    yield put(bulkAssignInsuredRisksFailed());
  }
}

export function* bulkUnassignInsuredRisksToPolicyWorker({ payload }) {
  const { insuredRisks, callback } = payload;

  try {
    const policiesFilters = yield select(selectPoliciesActiveFilters);
    const params = queryString.parse(policiesFilters);
    params.paging = false;
    const policiesResponse = yield call(onGetPolicies, params);

    const policyIds = policiesResponse.data.map((policy) => policy.id);

    const existingPairsResponse = yield call(onGetInsuredRisksPairs, insuredRisks);

    const pairIdsToDelete = existingPairsResponse.data
      .filter((pair) => policyIds.includes(pair.policy_id))
      .map((pair) => pair.id);

    if (pairIdsToDelete.length > 0) {
      yield call(onDeleteInsuredRisksForPolicy, pairIdsToDelete);
    }

    yield put(bulkUnassignInsuredRisksSucceeded());
    if (typeof callback === 'function') callback();
    yield put(reloadPolicies());

    const isPlural = insuredRisks?.length > 1;
    const message = isPlural
      ? policies_bulk_actions_remove_insured_risks_success
      : policies_bulk_actions_remove_insured_risk_success;

    renderNotification({
      type: 'success',
      message
    });
  } catch {
    yield put(bulkUnassignInsuredRisksFailed());
  }
}

export function* getInsuredRisksFromManyPoliciesWorker({ payload }) {
  const { policies } = payload;
  try {
    const promises = policies.map((policy) => onGetInsuredRisksToPolicies({ policy_id: [policy.id] }));
    const response = yield Promise.all(promises);
    let resultPayload = [];
    response.forEach((res) => {
      if (res.status === 200) {
        resultPayload = [...resultPayload, ...res.data];
      }
    });
    if (resultPayload.length > 0) {
      yield put(getInsuredRisksFromManyPoliciesSucceeded(resultPayload));
    } else {
      yield put(getInsuredRisksFromManyPoliciesFailed());
    }
  } catch {
    yield put(getInsuredRisksFromManyPoliciesFailed());
  }
}

export function* reloadInsuredRisksForSelectedPolicyWorker() {
  const policy = yield select(selectPolicy);
  if (!policy) {
    return;
  }
  const { id } = policy;
  yield put(getInsuredRisksForSelectedPolicy({ policyId: id }));
}

export default function* insuredRisksToPoliciesSaga() {
  yield all([
    takeLatest(getInsuredRisksForSelectedPolicy.type, getInsuredRisksForSelectedPolicyWorker),
    takeLatest(addInsuredRisksToPolicy.type, addInsuredRisksToPolicyWorker),
    takeLatest(updateInsuredRisksToPolicy.type, updateInsuredRisksToPolicyWorker),
    takeLatest(updateInsuredRisksToPolicySucceeded.type, reloadInsuredRisksForSelectedPolicyWorker),
    takeLatest(getInsuredRisksFromManyPolicies.type, getInsuredRisksFromManyPoliciesWorker),
    takeLatest(bulkAssignInsuredRisks.type, bulkAssignInsuredRisksToPolicyWorker),
    takeLatest(bulkUnassignInsuredRisks.type, bulkUnassignInsuredRisksToPolicyWorker)
  ]);
}
